import classnames from "classnames";
import PropTypes from "prop-types";
import styles from "./label.module.scss";
import ClearancePricing from "./clearance-pricing";
import { useSelector } from "react-redux";
import { isActiveMembership } from "helpers/membership-helpers";
import MoleculePrice from "./molecule-price";
import { showDecimal } from "helpers/price-helper";
import { AVAILABILITY_SCOPES, membershipLens } from "rtr-constants";

export function getRetailPricingLabel(isExclusiveDesign) {
  return isExclusiveDesign ? "Comparable Value" : "Original Retail";
}

function determinePricingMode(isActiveSubscriber, isClearance, isSubscriptionLens, isClassicLens, availabilityScope) {
  if (isClearance) {
    return "SHOW_CLEARANCE_PRICE";
  } else if ((isActiveSubscriber && isSubscriptionLens) || availabilityScope === AVAILABILITY_SCOPES.BUY) {
    // show comparable value price, Buy (KIF) Price
    return "SHOW_BUY_AND_MSRP_PRICE";
  } else if (isClassicLens) {
    return "SHOW_RENT_AND_MSRP_PRICE";
  } else {
    // show comparable value price
    return "SHOW_MSRP_PRICE";
  }
}

//sbenedict 7/25 - calculate this on the client to unblock Next Reserve grids
//Work on CSI-1296 will allow pricing info to be handled within Disco
const getRentalPriceAmount = (price = [], isActiveSubscriber) => {
  const { discount, value } = price.find(p => p.id === "rental") || {};

  if (!isActiveSubscriber) {
    return value;
  }

  if (!discount) {
    return value;
  }

  return value * (1 - discount);
};

const Label = ({ product }) => {
  const {
    buyPrice,
    clearanceDiscount,
    currencyCode = "USD",
    designerDisplayName,
    displayName,
    exclusiveDesign,
  } = product;

  const availabilityScope = useSelector(state => state.availabilityScope);
  const lens = useSelector(state => state.userData?.lens);
  const isClearance = useSelector(state => state.gridView?.isClearance);
  const membershipState = useSelector(state => state.membershipState);
  const isActiveSubscriber = isActiveMembership(membershipState);
  const isSubscriptionLens = lens === membershipLens.RTRUpdate || lens === membershipLens.unlimited;
  const isClassicLens = lens === membershipLens.classic;
  // Note: clearance grids use `product.msrp` (which is returned by the clearance endpoint), whereas
  // other endpoints like those that power curation grids return a complex object `product.price`
  return (
    <div className={classnames(styles.label)}>
      <div className={classnames(styles.label__wrapper)}>
        <p className={classnames(styles.label__title)}>{designerDisplayName}</p>
        <p className={classnames(styles.label__subtitle)}>{displayName}</p>
        {{
          SHOW_CLEARANCE_PRICE: (
            <>
              <ClearancePricing
                amount={buyPrice ?? product.price?.find(el => el?.id === "sale")?.value}
                comparableAmount={product.msrp ?? product.price?.find(el => el?.id === "msrp")?.value}
                comparableText={getRetailPricingLabel(exclusiveDesign)}
                currencyCode={currencyCode}
                discount={clearanceDiscount}
                showDecimal={showDecimal(buyPrice ?? product.price?.find(el => el?.id === "sale")?.value)}
              />
            </>
          ),
          SHOW_MSRP_PRICE: (
            <MoleculePrice
              priceTitle={getRetailPricingLabel(exclusiveDesign)}
              className={classnames(styles.label__prices)}
              amount={product.price?.find(el => el?.id === "msrp")?.value}
              currencyCode={currencyCode}
              showDecimal={showDecimal(product.price?.find(el => el?.id === "msrp")?.value)}
            />
          ),
          SHOW_BUY_AND_MSRP_PRICE: (
            <>
              <MoleculePrice
                priceTitle="Buy"
                className={classnames(styles.label__buy_price)}
                amount={product.price?.find(el => el?.id === "buy")?.value}
                currencyCode={currencyCode}
                showDecimal={showDecimal(product.price?.find(el => el?.id === "buy")?.value)}
              />
              <MoleculePrice
                priceTitle={getRetailPricingLabel(exclusiveDesign)}
                className={classnames(styles.label__prices)}
                amount={product.price?.find(el => el?.id === "msrp")?.value}
                currencyCode={currencyCode}
                showDecimal={showDecimal(product.price?.find(el => el?.id === "msrp")?.value)}
              />
            </>
          ),
          SHOW_RENT_AND_MSRP_PRICE: (
            <>
              <MoleculePrice
                priceTitle="To Rent"
                className={classnames(styles.label__buy_price)}
                amount={getRentalPriceAmount(product.price, isActiveSubscriber)}
                currencyCode={currencyCode}
                showDecimal={showDecimal(getRentalPriceAmount(product.price, isActiveSubscriber))}
              />
              <MoleculePrice
                priceTitle={getRetailPricingLabel(exclusiveDesign)}
                className={classnames(styles.label__prices)}
                amount={product.price?.find(el => el?.id === "msrp")?.value}
                currencyCode={currencyCode}
                showDecimal={showDecimal(product.price?.find(el => el?.id === "msrp")?.value)}
              />
            </>
          ),
        }[
          determinePricingMode(isActiveSubscriber, isClearance, isSubscriptionLens, isClassicLens, availabilityScope)
        ] || null}
      </div>
    </div>
  );
};

Label.propTypes = {
  product: PropTypes.object,
};

export default Label;
